import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  AddOrderItemModalRootScreenParamList,
  AddProductOrderItemRootScreenParamList,
} from "../../../../types";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { AddOrEditOrderItemScreen } from "../AddOrEditOrderItemScreen";
import { useLazyQuery } from "@apollo/client";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  QUERY_PRODUCT,
  SelectedProductEntityContext,
} from "../../../products/utils";
import {
  DataCoreBaseProduct,
  Product,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { OrderItemForm } from "../../components/OrderItemForm";
import {
  EditProductRootScreen,
  ProductSelectSupplierScreen,
} from "../../../products/screens";
import { useStockOrderContextProvider } from "../../utils/active-stock-order";
import { useQueryFindBaseProductByBarcodeAsync } from "../../../../utilities/retailer-data-core";

export type AddProductOrderItemRootScreenProps = StackScreenProps<
  AddOrderItemModalRootScreenParamList,
  "AddProductOrderItemRootScreen"
>;

export const AddProductOrderItemRootScreen = ({
  navigation,
  route,
}: AddProductOrderItemRootScreenProps) => {
  const { stockOrder } = useStockOrderContextProvider();

  const [checkedBaseProduct, setCheckedBaseProduct] =
    React.useState<boolean>(false);

  const [baseProduct, setBaseProduct] = React.useState<
    DataCoreBaseProduct | undefined
  >(undefined);

  const { query } = useQueryFindBaseProductByBarcodeAsync();

  const [productEntity, setProductEntity] = React.useState<Product | undefined>(
    undefined
  );

  const { productId, barcode } = route.params;

  const [q] = useLazyQuery<{ product: Product }>(QUERY_PRODUCT);
  const transitionEnded = useHasTransitionEnded(navigation);

  React.useEffect(() => {
    if (productId) {
      q({
        variables: {
          id: productId,
        },
      })
        .then((result) => {
          setProductEntity(result.data?.product);
        })
        .catch((err) => console.log(err));
    }
  }, []);

  React.useEffect(() => {
    if (!barcode) {
      setCheckedBaseProduct(false);
      return;
    }
    query(barcode)
      .then((baseProduct) => {
        setCheckedBaseProduct(true);
        setBaseProduct(baseProduct);
      })
      .catch(() => {
        setCheckedBaseProduct(false);
      })
      .finally(() => {
        setCheckedBaseProduct(true);
      });
  }, []);

  const Drawer =
    createStackHeaderNavigator<AddProductOrderItemRootScreenParamList>();

  if (
    false == transitionEnded ||
    (productId && !productEntity) ||
    (barcode && !checkedBaseProduct)
  ) {
    return (
      <PlaceholderLoadingScreen
        navigation={navigation}
        modifyHeader={false}
        content={ResourcePageSkeleton}
      />
    );
  }

  return (
    <OrderItemForm.Provider
      defaultFormValues={{
        id: undefined,
        productId: productEntity?.id,
        supplierCode: productEntity?.supplierStockCode ?? undefined,
        quantity: "0",
        supplierId: productEntity?.supplierId ?? undefined,
        stockOrderId: stockOrder.id,
        name: productEntity?.name ?? baseProduct?.stockDescription ?? "",
        barcode:
          productEntity?.stockNumber ?? baseProduct?.sku ?? barcode ?? "",
      }}
    >
      <Drawer.Navigator
        screenOptions={{
          animationEnabled: true,
          cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          headerLeft: () => <ModalCloseButton type="Cancel" />,
          title: "",
        }}
      >
        <Drawer.Screen
          name="AddOrEditOrderItemScreen"
          component={AddOrEditOrderItemScreen}
          initialParams={{ baseProductMatchNotice: !!baseProduct }}
        />
        <Drawer.Screen
          name="ProductSelectSupplierScreen"
          component={ProductSelectSupplierScreen}
        />
        <Drawer.Group
          screenOptions={{
            headerShown: false,
            presentation: "modal",
          }}
        >
          <Drawer.Screen
            name="EditProductRootScreen"
            component={EditProductRootScreen}
          />
        </Drawer.Group>
      </Drawer.Navigator>
    </OrderItemForm.Provider>
  );
};
